import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import localizationService from "../../services/localization-service";
import { validateSessionIdentity } from "../../actions/session.action";
import storageService from "../../services/storage-service";
import config from "../../config";

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 10 * 60 * 1000; // 10 minutes

const ModalSessionIdentity = ({ isOpen, onClose, onSuccess, redirectUrl }) => {
  const selectedlang = useSelector((state) => state.account.lang);
  // True when the logged-in user has a Kipu account; controls whether DOB is required at session join.
  // Falls back to sessionStorage so the value survives a browser refresh (Redux is in-memory only).
  const hasKippuAccIdFromStore = useSelector(
    (state) => state.account.loggedUser?.HasKippuAccId,
  );
  const hasKippuAccId =
    hasKippuAccIdFromStore != null
      ? Boolean(hasKippuAccIdFromStore)
      : storageService.getStorageItem(config.HAS_KIPPU_ACC_ID) === "true";
  const userId = storageService.getStorageItem(config.USER_ID);
  const attemptsKey = `session_identity_attempts_${userId ?? "default"}`;
  const lockedAtKey = `session_identity_locked_at_${userId ?? "default"}`;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // DOB is split into three numeric fields (MM / DD / YYYY) so mobile users get
  // a digit keypad instead of the year-scrolling wheel. Combined into a
  // MM/DD/YYYY string at submit time (US-style format expected by the backend).
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!userId) return;

    const stored = localStorage.getItem(attemptsKey);
    const count = stored ? parseInt(stored, 10) : 0;

    if (count >= MAX_ATTEMPTS) {
      const lockedAt = parseInt(localStorage.getItem(lockedAtKey) ?? "0", 10);
      if (Date.now() - lockedAt >= LOCK_DURATION_MS) {
        localStorage.removeItem(attemptsKey);
        localStorage.removeItem(lockedAtKey);
        setAttempts(0);
        return;
      }
    }

    setAttempts(count);
  }, [userId]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    localStorage.setItem(attemptsKey, attempts.toString());
  }, [userId, attempts, attemptsKey]);

  const locked = attempts >= MAX_ATTEMPTS;

  useEffect(() => {
    if (!locked) return;
    const lockedAt = parseInt(localStorage.getItem(lockedAtKey) ?? "0", 10);
    const remaining = LOCK_DURATION_MS - (Date.now() - lockedAt);
    if (remaining <= 0) {
      localStorage.removeItem(attemptsKey);
      localStorage.removeItem(lockedAtKey);
      setAttempts(0);
      return;
    }
    const timer = setTimeout(() => {
      localStorage.removeItem(attemptsKey);
      localStorage.removeItem(lockedAtKey);
      setAttempts(0);
    }, remaining);
    return () => clearTimeout(timer);
  }, [locked, attemptsKey, lockedAtKey]);

  if (!isOpen) return null;

  // Filters non-digits, caps length, and jumps to the next field once full.
  const handleDobChange = (value, setter, maxLen, nextRef) => {
    const digits = value.replace(/\D/g, "").slice(0, maxLen);
    setter(digits);
    if (digits.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  // Backspace on an empty field jumps focus back to the previous one.
  const handleDobKeyDown = (e, value, prevRef) => {
    if (e.key === "Backspace" && value === "" && prevRef?.current) {
      prevRef.current.focus();
    }
  };

  // Validates the three DOB fields locally so a typo (e.g. Feb 30, year "5")
  // doesn't waste one of the user's 5 verification attempts. Returns a
  // localized error string, or null when the date is valid.
  const validateDob = () => {
    const fr = selectedlang === "fr-CA";
    const currentYear = new Date().getFullYear();

    if (dobYear.length !== 4) {
      return fr ? "L'année doit comporter 4 chiffres." : "Year must be 4 digits.";
    }
    const year = parseInt(dobYear, 10);
    const month = parseInt(dobMonth, 10);
    const day = parseInt(dobDay, 10);

    if (year < 1900 || year > currentYear) {
      return fr
        ? `L'année doit être entre 1900 et ${currentYear}.`
        : `Year must be between 1900 and ${currentYear}.`;
    }
    if (month < 1 || month > 12) {
      return fr
        ? "Le mois doit être entre 1 et 12."
        : "Month must be between 1 and 12.";
    }
    if (day < 1 || day > 31) {
      return fr
        ? "Le jour doit être entre 1 et 31."
        : "Day must be between 1 and 31.";
    }
    // Round-trip through Date to catch invalid calendar dates (Feb 30, Apr 31).
    const d = new Date(year, month - 1, day);
    if (
      d.getFullYear() !== year ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return fr ? "Veuillez entrer une date valide." : "Please enter a valid date.";
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d > today) {
      return fr
        ? "La date de naissance ne peut pas être dans le futur."
        : "Date of birth cannot be in the future.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (locked || loading) return;
    setError(null);

    // Validate DOB locally first so typos don't burn a verification attempt
    // and we don't send malformed strings to the API.
    if (hasKippuAccId) {
      const dobError = validateDob();
      if (dobError) {
        setError(dobError);
        return;
      }
    }

    // iOS Safari blocks window.open() called after an await — the user-gesture
    // context is gone. Pre-open synchronously now and navigate it after the
    // validation resolves. Falls back to same-tab navigation if blocked.
    const popup = redirectUrl ? window.open("", "_blank") : null;

    setLoading(true);

    // Build MM/DD/YYYY only when DOB is collected; otherwise pass "" (the
    // backend ignores DOB for users without a Kipu account). Backend parses
    // this exact format via DateOnly.TryParseExact("MM/dd/yyyy", InvariantCulture).
    const dateOfBirth = hasKippuAccId
      ? `${dobMonth.padStart(2, "0")}/${dobDay.padStart(2, "0")}/${dobYear}`
      : "";

    try {
      const res = await validateSessionIdentity(
        firstName,
        lastName,
        dateOfBirth,
      );
      if (res.Valid) {
        setFirstName("");
        setLastName("");
        setDobDay("");
        setDobMonth("");
        setDobYear("");
        localStorage.removeItem(attemptsKey);
        localStorage.removeItem(lockedAtKey);
        setAttempts(0);
        if (redirectUrl) {
          if (popup && !popup.closed) {
            popup.location.href = redirectUrl;
          } else {
            window.location.href = redirectUrl;
          }
        }
        onSuccess();
      } else {
        if (popup && !popup.closed) popup.close();
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          localStorage.setItem(lockedAtKey, Date.now().toString());
          setError(null);
        } else {
          setError(
            selectedlang === "fr-CA"
              ? `Les informations fournies ne correspondent pas à nos dossiers. Il vous reste ${MAX_ATTEMPTS - newAttempts} tentative(s).`
              : `The information provided does not match our records. You have ${MAX_ATTEMPTS - newAttempts} attempt(s) remaining.`,
          );
        }
      }
    } catch {
      if (popup && !popup.closed) popup.close();
      setError(
        selectedlang === "fr-CA"
          ? "Une erreur est survenue. Veuillez réessayer."
          : "An error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setDobDay("");
    setDobMonth("");
    setDobYear("");
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {selectedlang === "fr-CA"
              ? "Vérification d'identité"
              : "Identity Verification"}
          </h3>
        </div>
        {locked ? (
          <>
            <div className="px-6 py-4">
              <p className="text-sm text-red-600 font-medium mb-2">
                {selectedlang === "fr-CA"
                  ? "Nombre maximum de tentatives atteint."
                  : "Maximum number of attempts reached."}
              </p>
              <p className="text-sm text-gray-600">
                {selectedlang === "fr-CA"
                  ? "Veuillez contacter le support Wagon à "
                  : "Please contact Wagon Support at "}
                <a
                  href="mailto:Wagonsupport@ehncanada.com"
                  className="text-[#9aa82d] underline font-medium"
                >
                  Wagonsupport@ehncanada.com
                </a>
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-center">
              <button
                onClick={handleClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedlang === "fr-CA" ? "Fermer" : "Close"}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-500">
                {selectedlang === "fr-CA"
                  ? "Veuillez confirmer votre identité pour rejoindre la session."
                  : "Please confirm your identity to join the session."}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedlang === "fr-CA" ? "Prénom" : "First Name"}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedlang === "fr-CA" ? "Nom de famille" : "Last Name"}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm"
                  required
                />
              </div>
              {hasKippuAccId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedlang === "fr-CA"
                      ? "Date de naissance"
                      : "Date of Birth"}
                  </label>
                  <div className="flex gap-2">
                    <input
                      ref={monthRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="bday-month"
                      placeholder={selectedlang === "fr-CA" ? "MM" : "MM"}
                      aria-label={selectedlang === "fr-CA" ? "Mois" : "Month"}
                      value={dobMonth}
                      onChange={(e) =>
                        handleDobChange(e.target.value, setDobMonth, 2, dayRef)
                      }
                      onKeyDown={(e) => handleDobKeyDown(e, dobMonth, null)}
                      maxLength={2}
                      size={2}
                      className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm text-center"
                      required
                    />
                    <input
                      ref={dayRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="bday-day"
                      placeholder={selectedlang === "fr-CA" ? "JJ" : "DD"}
                      aria-label={selectedlang === "fr-CA" ? "Jour" : "Day"}
                      value={dobDay}
                      onChange={(e) =>
                        handleDobChange(e.target.value, setDobDay, 2, yearRef)
                      }
                      onKeyDown={(e) => handleDobKeyDown(e, dobDay, monthRef)}
                      maxLength={2}
                      size={2}
                      className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm text-center"
                      required
                    />
                    <input
                      ref={yearRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="bday-year"
                      placeholder={selectedlang === "fr-CA" ? "AAAA" : "YYYY"}
                      aria-label={selectedlang === "fr-CA" ? "Année" : "Year"}
                      value={dobYear}
                      onChange={(e) =>
                        handleDobChange(e.target.value, setDobYear, 4, null)
                      }
                      onKeyDown={(e) => handleDobKeyDown(e, dobYear, dayRef)}
                      maxLength={4}
                      size={4}
                      className="flex-[2] min-w-0 px-2 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm text-center"
                      required
                    />
                  </div>
                </div>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-center space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] disabled:opacity-50"
              >
                {loading
                  ? selectedlang === "fr-CA"
                    ? "Vérification..."
                    : "Verifying..."
                  : selectedlang === "fr-CA"
                    ? "Vérifier"
                    : "Verify"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedlang === "fr-CA" ? "Annuler" : "Cancel"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ModalSessionIdentity;
